import React, { useEffect, useState } from "react";
import api, { API_BASE, getToken } from "@/lib/api";
import { BookOpen, Download } from "@phosphor-icons/react";
import { CurveWeaveGraphic, SunBloom, LotusBloom } from "@/components/vansha/Graphics";

export default function Yearbook() {
  const [preview, setPreview] = useState(null);
  const token = getToken();

  useEffect(() => { api.get("/yearbook/preview").then((r) => setPreview(r.data)); }, []);

  if (!preview) return <div className="text-ink-500">Loading your story…</div>;

  return (
    <div className="space-y-10" data-testid="yearbook-page">
      <header className="relative overflow-hidden vansha-card-tinted-sand p-8 sm:p-10">
        <div className="absolute right-0 -top-4 opacity-95 hidden md:block pointer-events-none">
          <LotusBloom size={220} />
        </div>
        <div className="relative z-10 max-w-2xl">
          <p className="overline text-terracotta mb-2">Signature</p>
          <h1 className="font-serif font-semibold text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            Your family <em className="text-terracotta font-normal italic">yearbook</em>
          </h1>
          <p className="text-ink-700 mt-4 leading-relaxed">A printed-quality book of everything you&apos;ve captured — timeline, letters, journal, milestones, memories. Auto-curated, beautifully bound.</p>
        </div>
      </header>

      <div className="grid lg:grid-cols-2 gap-8 items-stretch">
        <div className="vansha-card overflow-hidden bg-forest text-white relative" data-testid="yearbook-cover">
          <div className="absolute inset-0 opacity-30">
            <CurveWeaveGraphic width={520} height={280} />
          </div>
          <div className="absolute -top-8 -right-8 opacity-90 pointer-events-none">
            <SunBloom size={200} />
          </div>
          <div className="absolute top-6 right-6 w-24 h-24 rounded-full border border-white/20" />
          <div className="absolute bottom-8 left-8 w-16 h-16 rounded-full border border-white/15" />
          <div className="p-10 lg:p-14 relative min-h-[480px] flex flex-col">
            <div>
              <p className="overline" style={{ color: "#F4A72C" }}>Vansha presents</p>
              <p className="overline mt-1" style={{ color: "#F4A72C" }}>A family yearbook</p>
            </div>
            <div className="flex-1 flex flex-col justify-center">
              <h2 className="font-serif font-semibold text-5xl lg:text-6xl leading-tight">{preview.title}</h2>
              <p className="font-serif italic text-2xl mt-4" style={{ color: "#F4A72C" }}>{preview.subtitle}</p>
            </div>
            <p className="overline" style={{ color: "#F4A72C" }}>{new Date().getFullYear()} edition</p>
          </div>
        </div>

        <div className="space-y-6">
          <div className="vansha-card p-8" data-testid="yearbook-stats">
            <h3 className="font-serif text-2xl mb-4">Inside this edition</h3>
            <div className="grid grid-cols-2 gap-4">
              <Stat label="Timeline moments" value={preview.counts.timeline_events} />
              <Stat label="Letters" value={preview.counts.letters} />
              <Stat label="Journal entries" value={preview.counts.journal_entries} />
              <Stat label="Milestones" value={preview.counts.milestones} />
              <Stat label="Memories (photos)" value={preview.counts.memories} />
            </div>
          </div>

          <div className="vansha-card p-8" data-testid="yearbook-actions">
            <BookOpen weight="thin" size={28} className="text-terracotta mb-3" />
            <h3 className="font-serif text-xl mb-2">Download your PDF</h3>
            <p className="text-sm text-ink-700 mb-5">A beautifully typeset PDF — perfect to print, gift, or archive forever. Generated live from your story.</p>
            <a href={`${API_BASE}/yearbook/generate?auth=${token}`}
              onClick={async (e) => {
                e.preventDefault();
                const r = await api.get("/yearbook/generate", { responseType: "blob" });
                const url = URL.createObjectURL(r.data);
                const a = document.createElement("a");
                a.href = url;
                a.download = "vansha-yearbook.pdf";
                a.click();
                URL.revokeObjectURL(url);
              }}
              className="btn-primary inline-flex"
              data-testid="yearbook-download">
              <Download weight="thin" size={18} /> Download PDF
            </a>
          </div>

          <div className="vansha-card p-8 bg-vansha-accent" data-testid="yearbook-print">
            <p className="overline text-saffron mb-2">Signature members</p>
            <h3 className="font-serif text-xl mb-2">Printed hardcover annually</h3>
            <p className="text-sm text-ink-700">Members of Vansha Signature receive a beautifully printed hardcover edition each year — delivered to your home.</p>
          </div>
        </div>
      </div>

      {preview.sample_spread?.first_letter && (
        <section data-testid="yearbook-sample">
          <p className="overline text-saffron mb-3">Sample spread</p>
          <div className="vansha-card p-10 sm:p-16">
            <p className="overline mb-2">A letter from {preview.sample_spread.first_letter.from_name}</p>
            <h3 className="font-serif text-3xl mb-4">{preview.sample_spread.first_letter.title}</h3>
            <p className="font-serif text-lg leading-relaxed whitespace-pre-wrap text-ink-900">
              {preview.sample_spread.first_letter.body}
            </p>
          </div>
        </section>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <p className="font-serif text-4xl text-terracotta">{value}</p>
      <p className="overline mt-1">{label}</p>
    </div>
  );
}
